import { 
  BookOpen, 
  Zap, 
  Settings, 
  MapPin, 
  Navigation, 
  Shield, 
  Globe,
  Code,
  Target,
  Compass,
  Map,
  Building,
  Hash,
  Crosshair,
  Network,
  Lock,
  Server,
  Clock,
  Users
} from 'lucide-react';

export const documentationData = {
  sections: [
    {
      slug: 'residential-proxies',
      title: 'Residential Proxies',
      description: 'Complete guide to using IP9 residential proxies for web scraping, data collection, and automation.',
      icon: BookOpen,
      content: [
        {
          type: 'paragraph',
          text: 'IP9 Residential Proxies contain real IP addresses provided by Internet Service Providers (ISPs). These IP addresses are attached to physical devices across the globe at a country or city level. Therefore, it is easy to replicate human behaviour and avoid blocks using this type of proxy.'
        },
        {
          type: 'paragraph',
          text: 'IP9 Residential Proxies are fully equipped, rotate automatically, and are easy to set up. Our network provides access to over 300 million residential IP addresses from 200+ countries and 1000+ cities worldwide.'
        },
        {
          type: 'heading',
          level: 2,
          text: 'Key Features'
        },
        {
          type: 'list',
          items: [
            'Over 300 million residential IP addresses',
            'Coverage in 200+ countries and 1000+ cities',
            '99.9% uptime with high-speed connections',
            'Session control and sticky sessions',
            'Advanced targeting by country, city, state, and ASN',
            'Automatic IP rotation'
          ]
        },
        {
          type: 'heading',
          level: 2,
          text: 'Quick Start'
        },
        {
          type: 'paragraph',
          text: 'Get started with residential proxies in just a few minutes. Use https://httpbin.org/ip to test your connection:'
        },
        {
          type: 'code',
          language: 'bash',
          code: `# Test your connection
curl -x residential.ip9.io:8000 -U "username:password" https://httpbin.org/ip

# Response
{
  "origin": "203.45.67.89"  # Your residential IP
}`
        },
        {
          type: 'note',
          title: 'Authentication Required',
          text: 'All requests must include your username and password. You can find these credentials in your IP9 dashboard.'
        }
      ],
      children: [
        {
          slug: 'getting-started',
          title: 'Getting Started',
          description: 'Learn the basics of setting up and using IP9 residential proxies.',
          icon: Zap,
          content: [
            {
              type: 'paragraph',
              text: 'Make a request to https://httpbin.org/ip using a residential proxy from a random location through our backconnect entry. Replace USERNAME and PASSWORD with your proxy user credentials. Keep in mind that each request returns a new IP address as IP9 Residential Proxies rotate automatically.'
            },
            {
              type: 'paragraph',
              text: 'Use httpbin.org/ip to check the parameters of your IPs—this domain delivers information including IP address, provider, country, city, and additional geolocation data.'
            },
            {
              type: 'heading',
              level: 2,
              text: 'Basic Setup'
            },
            {
              type: 'paragraph',
              text: 'Our Residential Proxies support HTTP protocol:'
            },
            {
              type: 'code',
              language: 'bash',
              code: `curl -x residential.ip9.io:8000 -U "username:password" https://httpbin.org/ip`
            },
            {
              type: 'heading',
              level: 2,
              text: 'Country Targeting'
            },
            {
              type: 'paragraph',
              text: 'Now make a request using a proxy from France. All you need to do is add country-FR to your username string:'
            },
            {
              type: 'code',
              language: 'bash',
              code: `curl -x residential.ip9.io:8000 -U "username:password-country-FR" https://httpbin.org/ip`
            },
            {
              type: 'heading',
              level: 2,
              text: 'City-Level Targeting'
            },
            {
              type: 'paragraph',
              text: 'Here is an example of a request that goes through a proxy from London, United Kingdom. Adding country-GB-city-london to the username string enables you to specify a city:'
            },
            {
              type: 'code',
              language: 'bash',
              code: `curl -x residential.ip9.io:8000 -U "username:password-country-GB-city-london" https://httpbin.org/ip`
            },
            {
              type: 'heading',
              level: 2,
              text: 'Session Control'
            },
            {
              type: 'paragraph',
              text: 'If you need to keep the same proxy for more than one request, use session control. Add session-randomString123 to your username string, where randomString123 can be any random string:'
            },
            {
              type: 'code',
              language: 'bash',
              code: `curl -x residential.ip9.io:8000 -U "username:password-session-randomString123" https://httpbin.org/ip`
            },
            {
              type: 'note',
              title: 'Dashboard Access',
              text: 'To find out your usage statistics, generated traffic, request count, and more, please login to your IP9 dashboard. You can also add proxy users (sub-users) and track their statistics separately.'
            },
            {
              type: 'paragraph',
              text: 'If you have any additional questions, please contact our support team at support@ip9.io.'
            }
          ]
        },
        {
          slug: 'making-requests',
          title: 'Making Requests',
          description: 'Detailed guide on making HTTP requests through IP9 residential proxies.',
          icon: Code,
          content: [
            {
              type: 'heading',
              level: 2,
              text: 'Basic Query'
            },
            {
              type: 'paragraph',
              text: 'Basic query only requires passing username and password. No other parameters are needed. Such a query will result in the request being made from a random IP address (proxy). Every new request will use a different proxy.'
            },
            {
              type: 'heading',
              level: 3,
              text: 'Code Examples'
            },
            {
              type: 'paragraph',
              text: 'In this example, a query to httpbin.org/ip is performed from a random IP:'
            },
            {
              type: 'code',
              language: 'bash',
              title: 'cURL',
              code: `curl -x residential.ip9.io:8000 -U "username:password" https://httpbin.org/ip`
            },
            {
              type: 'code',
              language: 'php',
              title: 'PHP',
              code: `<?php
$proxy = 'residential.ip9.io:8000';
$username = 'USERNAME';
$password = 'PASSWORD';
$query = curl_init('https://httpbin.org/ip');
curl_setopt($query, CURLOPT_RETURNTRANSFER, 1);
curl_setopt($query, CURLOPT_PROXY, "http://$proxy");
curl_setopt($query, CURLOPT_PROXYUSERPWD, "$username:$password");
$output = curl_exec($query);
curl_close($query);
if ($output) echo $output;
?>`
            },
            {
              type: 'code',
              language: 'python',
              title: 'Python',
              code: `import urllib.request
username = 'USERNAME'
password = 'PASSWORD'
entry = ('http://%s:%s@residential.ip9.io:8000' % (username, password))
query = urllib.request.ProxyHandler({
    'http': entry,
    'https': entry,
})
execute = urllib.request.build_opener(query)
print(execute.open('https://httpbin.org/ip').read())`
            },
            {
              type: 'heading',
              level: 2,
              text: 'Advanced Configuration'
            },
            {
              type: 'paragraph',
              text: 'A single backconnect proxy (residential.ip9.io:8000) enables you to choose a specific country or city proxy via additional parameters in the username. This approach also supports session control. Here is a sample of credentials:'
            },
            {
              type: 'code',
              language: 'text',
              code: `username:password-country-US-city-CITY-session-abcde12345
username:password-country-US-city-CITY-session-abcde12345-sesstime-5`
            },
            {
              type: 'heading',
              level: 3,
              text: 'Query Parameters'
            },
            {
              type: 'table',
              headers: ['Parameter', 'Description'],
              rows: [
                ['username', 'Your IP9 username'],
                ['country', 'Case insensitive country code in 2-letter ISO 3166-1 alpha-2 format. For example, DE for Germany, GB for the United Kingdom, TH for Thailand.'],
                ['city', 'Case insensitive city name in English. This parameter must be accompanied by country for better accuracy, for example, country-GB-city-london for London, United Kingdom.'],
                ['state', 'Case insensitive US state name with us_ in the beginning, for example, us_california, us_illinois.'],
                ['session', 'Session ID to keep the same IP with upcoming queries. The session expires in 10 minutes. After that, a new IP address is assigned to that session ID.'],
                ['sesstime', 'The session time parameter keeps the same IP for a certain period. The maximum session time is 30 minutes.'],
                ['password', 'Your IP9 password (required)']
              ]
            }
          ]
        },
        {
          slug: 'location-settings',
          title: 'Location Settings',
          description: 'Configure proxy location targeting for specific countries, cities, and regions.',
          icon: MapPin,
          content: [
            {
              type: 'paragraph',
              text: 'IP9 Residential proxy network covers 200+ locations and offers country, city, state, continent, ZIP code, coordinate-level, and ASN targeting.'
            },
            {
              type: 'paragraph',
              text: 'You can find more information on selecting geo-location parameters in the pages listed below:'
            },
            {
              type: 'list',
              items: [
                'Country - Target specific countries',
                'City - Target specific cities within countries',
                'State - Target US states specifically',
                'Continent - Target entire continents',
                'ZIP/Postal Code - Ultra-precise postal code targeting',
                'Coordinates - Target specific geographical coordinates',
                'ASN Targeting - Target specific Internet Service Providers'
              ]
            },
            {
              type: 'heading',
              level: 2,
              text: 'Country, State, City Mapping'
            },
            {
              type: 'paragraph',
              text: 'We use the MaxMind GeoIP2 database to assign our exit nodes country, state, and city values. This database is updated weekly.'
            },
            {
              type: 'paragraph',
              text: 'Some other public IP databases may show different locations. In cases where there can not be any location mismatches, please double-check if the location of the assigned proxy is the same on the IP database used by the target website.'
            },
            {
              type: 'paragraph',
              text: 'If you have any additional questions, please contact your account manager or our support team at support@ip9.io.'
            }
          ],
          children: [
            {
              slug: 'country',
              title: 'Country',
              description: 'Find country parameters and specific country entry nodes.',
              icon: Globe,
              content: [
                {
                  type: 'paragraph',
                  text: 'Adding a country flag to the authorization header enables one to specify which country IP to use to process the request. The value of this parameter is a case insensitive country code in 2-letter ISO 3166-1 alpha-2 format. For example, DE for Germany, GB for the United Kingdom, and TH for Thailand proxy.'
                },
                {
                  type: 'paragraph',
                  text: 'Here are a few examples of country parameters:'
                },
                {
                  type: 'table',
                  headers: ['Country', 'Country Parameter'],
                  rows: [
                    ['United States', 'country-US'],
                    ['Italy', 'country-IT'],
                    ['Thailand', 'country-TH'],
                    ['Japan', 'country-JP'],
                    ['Australia', 'country-AU'],
                    ['Spain', 'country-ES']
                  ]
                },
                {
                  type: 'heading',
                  level: 2,
                  text: 'Code Example'
                },
                {
                  type: 'paragraph',
                  text: 'In this example, a query to httpbin.org/ip is performed from a random IP address from Germany:'
                },
                {
                  type: 'code',
                  language: 'bash',
                  title: 'cURL',
                  code: `curl -x residential.ip9.io:8000 -U "username:password-country-DE" https://httpbin.org/ip`
                },
                {
                  type: 'code',
                  language: 'php',
                  title: 'PHP',
                  code: `<?php
$username = 'USERNAME';
$password = 'PASSWORD';
$country = 'DE';
$proxy = 'residential.ip9.io:8000';
$query = curl_init('https://httpbin.org/ip');
curl_setopt($query, CURLOPT_RETURNTRANSFER, 1);
curl_setopt($query, CURLOPT_PROXY, "http://$proxy");
curl_setopt($query, CURLOPT_PROXYUSERPWD, "$username:$password-country-$country");
$output = curl_exec($query);
curl_close($query);
if ($output)
    echo $output;
?>`
                },
                {
                  type: 'code',
                  language: 'python',
                  title: 'Python',
                  code: `import urllib.request
import random
username = 'USERNAME'
password = 'PASSWORD'
country = 'DE'
entry = ('http://%s-country-%s:%s@residential.ip9.io:8000' %
    (username, country, password))
query = urllib.request.ProxyHandler({
    'http': entry,
    'https': entry,
})
execute = urllib.request.build_opener(query)
print(execute.open('https://httpbin.org/ip').read())`
                },
                {
                  type: 'heading',
                  level: 2,
                  text: 'Country Specific Entry Nodes'
                },
                {
                  type: 'paragraph',
                  text: 'IP9 residential network has coverage in almost every country in the world. A random country-specific proxy entry point will return new IP with every new request.'
                },
                {
                  type: 'paragraph',
                  text: 'You do not need to pass any additional parameters, just username:password. If you have whitelisted IPs, you do not need to pass login credentials. Country-specific ports do not support city-level results.'
                },
                {
                  type: 'note',
                  title: 'Entry Points',
                  text: 'Please see your IP9 dashboard for complete entry point information and download options.'
                }
              ]
            },
            {
              slug: 'city',
              title: 'City',
              description: 'Target specific cities for precise location targeting.',
              icon: Building,
              content: [
                {
                  type: 'paragraph',
                  text: 'Add a city parameter to narrow down to city-level targeting. For example, country-DE-city-munich means that a proxy from Munich, Germany, will handle the query.'
                },
                {
                  type: 'paragraph',
                  text: 'We support every city in the world, yet due to the dynamic nature of Residential Proxies we do not guarantee we will have proxies there at specific moment in time. Most popular cities are well covered.'
                },
                {
                  type: 'paragraph',
                  text: 'Here are a few examples of valid combinations of country and city parameters:'
                },
                {
                  type: 'list',
                  items: [
                    'country-US-city-los_angeles',
                    'country-IT-city-rome', 
                    'country-TH-city-bangkok',
                    'country-JP-city-tokyo',
                    'country-AU-city-sydney',
                    'country-ES-city-barcelona'
                  ]
                },
                {
                  type: 'heading',
                  level: 2,
                  text: 'Code Examples'
                },
                {
                  type: 'paragraph',
                  text: 'In this example, a query to httpbin.org/ip is performed from a random IP address from Munich, Germany:'
                },
                {
                  type: 'code',
                  language: 'bash',
                  title: 'cURL',
                  code: `curl -x residential.ip9.io:8000 -U "username:password-country-DE-city-munich" https://httpbin.org/ip`
                },
                {
                  type: 'code',
                  language: 'php',
                  title: 'PHP',
                  code: `<?php
$username = 'USERNAME';
$password = 'PASSWORD';
$country = 'DE';
$city = 'munich';
$proxy = 'residential.ip9.io:8000';
$query = curl_init('https://httpbin.org/ip');
curl_setopt($query, CURLOPT_RETURNTRANSFER, 1);
curl_setopt($query, CURLOPT_PROXY, "http://$proxy");
curl_setopt($query, CURLOPT_PROXYUSERPWD, "$username-country-$country-city-$city:$password");
$output = curl_exec($query);
curl_close($query);
if ($output)
    echo $output;
?>`
                },
                {
                  type: 'code',
                  language: 'python',
                  title: 'Python',
                  code: `import urllib.request
import random
username = 'USERNAME'
password = 'PASSWORD'
country = 'DE'
city = 'munich'
entry = ('http://%s-country-%s-city-%s:%s@residential.ip9.io:8000' %
    (username, country, city, password))
query = urllib.request.ProxyHandler({
    'http': entry,
    'https': entry,
})
execute = urllib.request.build_opener(query)
print(execute.open('https://httpbin.org/ip').read())`
                },
                {
                  type: 'note',
                  title: 'City List',
                  text: 'Contact our support team at support@ip9.io to get the complete list of supported cities or check your dashboard for city availability.'
                }
              ]
            },
            {
              slug: 'state',
              title: 'State',
              description: 'Target specific US states with residential proxies.',
              icon: Map,
              content: [
                {
                  type: 'paragraph',
                  text: 'To get a proxy from a specific US state, add the state flag to the authorization string. For example, state-us_california, state-us_illinois.'
                },
                {
                  type: 'paragraph',
                  text: 'Here are a few examples of valid combinations of state parameters:'
                },
                {
                  type: 'list',
                  items: [
                    'state-us_california-city-los_angeles',
                    'state-us_illinois-city-springfield',
                    'state-us_michigan-city-detroit',
                    'state-us_ohio-city-columbus',
                    'state-us_maine-city-portland',
                    'state-us_tennessee-city-memphis'
                  ]
                },
                {
                  type: 'heading',
                  level: 2,
                  text: 'Code Example'
                },
                {
                  type: 'paragraph',
                  text: 'In this example, a query to httpbin.org/ip is performed from a random IP address from California, USA:'
                },
                {
                  type: 'code',
                  language: 'bash',
                  title: 'cURL',
                  code: `curl -x residential.ip9.io:8000 -U "username:password-state-us_california" https://httpbin.org/ip`
                },
                {
                  type: 'code',
                  language: 'php',
                  title: 'PHP',
                  code: `<?php
$username = 'USERNAME';
$password = 'PASSWORD';
$state = 'us_california';
$proxy = 'residential.ip9.io:8000';
$query = curl_init('https://httpbin.org/ip');
curl_setopt($query, CURLOPT_RETURNTRANSFER, 1);
curl_setopt($query, CURLOPT_PROXY, "http://$proxy");
curl_setopt($query, CURLOPT_PROXYUSERPWD, "$username-state-$state:$password");
$output = curl_exec($query);
curl_close($query);
if ($output)
    echo $output;
?>`
                },
                {
                  type: 'code',
                  language: 'python',
                  title: 'Python',
                  code: `import urllib.request
import random
username = 'USERNAME'
password = 'PASSWORD'
state = 'us_california'
entry = ('http://%s-state-%s:%s@residential.ip9.io:8000' %
    (username, state, password))
query = urllib.request.ProxyHandler({
    'http': entry,
    'https': entry,
})
execute = urllib.request.build_opener(query)
print(execute.open('https://httpbin.org/ip').read())`
                },
                {
                  type: 'note',
                  title: 'US States List',
                  text: 'Contact our support team at support@ip9.io for a complete list of supported US states.'
                }
              ]
            },
            {
              slug: 'continent',
              title: 'Continent',
              description: 'Target entire continents for broad geographical coverage.',
              icon: Globe,
              content: [
                {
                  type: 'paragraph',
                  text: 'Continental targeting allows you to route requests through proxies from entire continents for broader geographic coverage. This is useful when you need regional diversity without specifying exact countries.'
                },
                {
                  type: 'heading',
                  level: 2,
                  text: 'Supported Continents'
                },
                {
                  type: 'list',
                  items: [
                    'North America (NA)',
                    'Europe (EU)', 
                    'Asia (AS)',
                    'South America (SA)',
                    'Africa (AF)',
                    'Oceania (OC)'
                  ]
                },
                {
                  type: 'code',
                  language: 'bash',
                  code: `# Europe
curl -x residential.ip9.io:8000 -U "username:password-continent-EU" https://httpbin.org/ip

# North America
curl -x residential.ip9.io:8000 -U "username:password-continent-NA" https://httpbin.org/ip`
                }
              ]
            },
            {
              slug: 'zip-postal-code',
              title: 'ZIP/Postal Code',
              description: 'Ultra-precise targeting using postal codes.',
              icon: Hash,
              content: [
                {
                  type: 'paragraph',
                  text: 'ZIP/Postal code targeting provides the most precise geographical targeting available, allowing you to route requests through proxies in specific postal code areas.'
                },
                {
                  type: 'paragraph',
                  text: 'This targeting method is available for supported countries and provides the highest level of geographic precision possible.'
                },
                {
                  type: 'code',
                  language: 'bash',
                  code: `# US ZIP code
curl -x residential.ip9.io:8000 -U "username:password-country-US-zip-90210" https://httpbin.org/ip

# UK Postal code
curl -x residential.ip9.io:8000 -U "username:password-country-GB-postal-SW1A" https://httpbin.org/ip`
                },
                {
                  type: 'note',
                  title: 'Availability',
                  text: 'ZIP/Postal code targeting is subject to proxy availability in specific areas. Contact our support team at support@ip9.io for coverage information.'
                }
              ]
            },
            {
              slug: 'coordinates',
              title: 'Coordinates',
              description: 'Target specific geographical coordinates.',
              icon: Crosshair,
              content: [
                {
                  type: 'paragraph',
                  text: 'Coordinate targeting allows you to specify exact latitude and longitude coordinates for the most precise geographical targeting available.'
                },
                {
                  type: 'paragraph',
                  text: 'This advanced targeting method is useful when you need proxies from very specific geographical locations based on precise coordinates.'
                },
                {
                  type: 'code',
                  language: 'bash',
                  code: `# London coordinates (51.5074° N, 0.1278° W)
curl -x residential.ip9.io:8000 -U "username:password-lat-51.5074-lon-0.1278" https://httpbin.org/ip`
                },
                {
                  type: 'note',
                  title: 'Precision',
                  text: 'Coordinate targeting depends on proxy availability near the specified coordinates. Results may vary based on network coverage.'
                }
              ]
            },
            {
              slug: 'asn-targeting',
              title: 'ASN Targeting',
              description: 'Target specific Internet Service Providers using ASN numbers.',
              icon: Network,
              content: [
                {
                  type: 'paragraph',
                  text: 'Our Residential Proxies support targeting by ASN number which means that you can choose proxies from specific carriers. You have to enter the required ASN number in your request.'
                },
                {
                  type: 'paragraph',
                  text: 'Below is the example for T-Mobile, its ASN number in the US being 21928:'
                },
                {
                  type: 'code',
                  language: 'bash',
                  code: `curl -x residential.ip9.io:8000 -U "username:password-asn-21928-session-abcde12345" https://httpbin.org/ip`
                },
                {
                  type: 'paragraph',
                  text: 'You can find ASN numbers using online ASN lookup tools. Residential Proxies pool is dynamic in its nature so if there are no IPs under that specific ASN at that moment, you will receive a 502 Bad Gateway response code. In such case, you should try another ASN number.'
                },
                {
                  type: 'note',
                  title: 'Important',
                  text: 'You can use either Country or ASN parameter per each request. If both location parameters are used, only Country parameter will be applied.'
                }
              ]
            }
          ]
        },
        {
          slug: 'session-control',
          title: 'Session Control',
          description: 'Learn how to maintain the same IP address across multiple requests.',
          icon: Lock,
          content: [
            {
              type: 'heading',
              level: 2,
              text: 'Establishing Session'
            },
            {
              type: 'paragraph',
              text: 'The session ID parameter allows you to keep the same IP address to run multiple requests. To reuse the same IP multiple times, use session parameter after username with randomly created alphanumeric string, for example, session-abcde12345.'
            },
            {
              type: 'paragraph',
              text: 'The standard session time is 10 minutes or up to 60s of inactivity (no requests). After that, a new IP address is assigned automatically.'
            },
            {
              type: 'code',
              language: 'bash',
              code: `curl -x residential.ip9.io:8000 -U "username:password-country-DE-session-abcde12345" https://httpbin.org/ip`
            },
            {
              type: 'heading',
              level: 2,
              text: 'Session Time'
            },
            {
              type: 'paragraph',
              text: 'You can use the sesstime parameter to maintain the same IP for up to 1440 minutes (24 hours). However, because the residential proxy pool is dynamic, your connection may end sooner.'
            },
            {
              type: 'code',
              language: 'bash',
              code: `curl -x residential.ip9.io:8000 -U "username:password-country-DE-session-abcde12345-sesstime-7" https://httpbin.org/ip`
            }
          ]
        },
        {
          slug: 'protocols',
          title: 'Protocols',
          description: 'HTTP',
          icon: Settings,
          content: [
            {
              type: 'paragraph',
              text: 'IP9 residential proxies support HTTP protocol.'
            },
            {
              type: 'heading',
              level: 2,
              text: 'HTTP'
            },
            {
              type: 'paragraph',
              text: 'Our documentation includes code examples showcasing how to connect to the proxies using HTTP protocol. The protocol is supported by common libraries and third-party software.'
            },
            {
              type: 'code',
              language: 'bash',
              code: `curl -x residential.ip9.io:8000 -U "username:password" https://httpbin.org/ip`
            },
            {
              type: 'heading',
              level: 2,
              text: 'Ports of Proxies'
            },
            {
              type: 'paragraph',
              text: 'The default configuration of proxy ports includes 80 and 443, which are standard ports designed to accommodate the HTTP and HTTPS protocols, as outlined in the table below. To access other ports, it is necessary to undergo compliance verification, please contact our support team at support@ip9.io.'
            },
            {
              type: 'table',
              headers: ['Protocol', 'Port'],
              rows: [
                ['HTTP', '80'],
                ['HTTPS', '443']
              ]
            }
          ]
        },
        {
          slug: 'restricted-targets',
          title: 'Restricted Targets',
          description: 'Information about websites and services that may have restrictions.',
          icon: Shield,
          content: [
            {
              type: 'paragraph',
              text: 'Some websites are restricted on our IP9 Residential Proxy network to avoid abuse and unclear activities.'
            },
            {
              type: 'paragraph',
              text: 'Some targets can be unblocked after going through a short KYC procedure (including Google domains). Get in touch with our Customer Support Team at support@ip9.io if your target falls under the mentioned groups to ensure you can access the target before purchasing.'
            },
            {
              type: 'note',
              title: 'Important',
              text: 'The list includes but is not limited to targets such as:'
            },
            {
              type: 'heading',
              level: 2,
              text: 'Restricted Target Categories'
            },
            {
              type: 'table',
              headers: ['Target Category', 'Example'],
              rows: [
                ['Entertainment & streaming', 'Netflix, Spotify, Twitch, Disney'],
                ['Banking and other financial institutions', 'PayPal, Bank of America, Binance'],
                ['Government websites', 'usa.gov, canada.ca'],
                ['Gaming', 'Playstation, Steam'],
                ['Ticketing', 'Ticketmaster, Eventbrite'],
                ['Mailing', 'Outlook, Yahoo mail'],
                ['Ads', 'Google Tag Manager, Doubleclick'],
                ['IP checking websites*', 'ipinfo.info, iplocation.net']
              ]
            },
            {
              type: 'note',
              title: 'IP Checking',
              text: '*Please use httpbin.org/ip to check your IP address when using IP9 proxies.'
            },
            {
              type: 'heading',
              level: 2,
              text: 'Important Notice'
            },
            {
              type: 'paragraph',
              text: 'Pay-as-you-go is non-refundable, even if the target is unsupported. Please confirm compatibility with Customer Success before purchasing.'
            },
            {
              type: 'paragraph',
              text: 'Get in touch with our customer support team at support@ip9.io or via the 24/7 live chat on our website.'
            }
          ]
        }
      ]
    }
  ]
};